import React, { useContext, useEffect, useState } from "react";
import { Col, Button, Radio } from "antd";
import { Controller } from "react-hook-form";
import FormItem from "../form-item";
import ImageUploader from "../../form-components/image-uploader-client";
import Image from "../antd/image";
import { useTranslation } from "react-i18next";
import { MediumFor } from "../../../models/medium/enum";
import { IMedium } from "../../../models/medium/response";
import { DeleteOutlined, LoadingOutlined } from "@ant-design/icons";
import AppContext from "../../../context/app/context";
import { getImageProcessingMode, ImageProcessingMode } from "../../../utils/helpers/media-upload";

interface Props {
    fieldName: string;
    currentId?: any;
    fieldValue?: any;
    currentImage: any;
    newImageUrl: string | null;
    isChanged: boolean;
    onUpload: () => void;
    onImageChange: (response: IMedium | null) => void;
    setWithoutBackgrounds?: (value: boolean) => void;
    preserveOriginal?: boolean;
    setPreserveOriginal?: (value: boolean) => void;
    onDeleteImage: () => void | Promise<void>;
    onRemoveNewImage?: (restoreCurrentImage: boolean) => void | Promise<void>;
    isImageLoading: boolean;
    notRequired?: boolean;
    title?: string;
    value?: any
    force?:boolean
    isNotification?: boolean
}

const ImageFieldBlock: React.FC<Props> = ({
    fieldName,
    currentId,
    currentImage,
    newImageUrl,
    isChanged,
    onUpload,
    onImageChange,
    setWithoutBackgrounds,
    preserveOriginal,
    setPreserveOriginal,
    onDeleteImage,
    onRemoveNewImage,
    isImageLoading,
    notRequired = false,
    title = 'image',
    value,
    isNotification
}) => {
    const { t } = useTranslation();
    const { direction } = useContext(AppContext);
    const [isCurrentImageHidden, setIsCurrentImageHidden] = useState(false);
    const showProcessingMode = Boolean(setPreserveOriginal && !isNotification && isChanged && newImageUrl);
    const processingMode = getImageProcessingMode(Boolean(value), Boolean(preserveOriginal));
    const currentImageVisible = currentImage && !isCurrentImageHidden;
    const deleteButtonPosition = direction === 'rtl' ? { left: 8 } : { right: 8 };

    useEffect(() => {
        setIsCurrentImageHidden(false);
    }, [currentImage?.id]);

    return (
        <React.Fragment key={fieldName}>
            <Col span={24}>
                <FormItem label={t(title)}>
                    <Controller
                        name={fieldName}
                        rules={{ required: !notRequired && true }}
                        render={({ field }) => (
                            <ImageUploader
                                {...field}
                                type="DRAGGER"
                                imageFor={MediumFor.UserProfile}
                                preserveOriginal={isNotification ? false : preserveOriginal}
                                onChange={(response) => {
                                    if (response) {
                                        setWithoutBackgrounds?.(false);
                                    } else {
                                        setWithoutBackgrounds?.(Boolean(currentImage?.without_background));
                                    }
                                    setPreserveOriginal?.(false);
                                    onImageChange(response);
                                    field.onChange(response ? response.id : isCurrentImageHidden ? undefined : currentId);
                                }}
                            />
                        )}
                    />
                </FormItem>
            </Col>

            <div
                style={{
                    marginTop: 16,
                    display: "flex",
                    flexWrap: "wrap",
                    justifyContent: "space-between",
                    gap: 32,
                }}
            >
                {currentImageVisible && (
                    <div style={{ textAlign: "center", flex: "1 1 300px" }}>
                        <h4>{t("old_image")}</h4>
                        <div style={{ position: "relative" }}>
                            {!newImageUrl && (
                                <Button
                                    type="text"
                                    danger
                                    shape="circle"
                                    icon={isImageLoading ? <LoadingOutlined /> : <DeleteOutlined />}
                                    loading={isImageLoading}
                                    aria-label={t("delete")}
                                    title={t("delete")}
                                    onClick={async () => {
                                        await onDeleteImage();
                                        setIsCurrentImageHidden(true);
                                    }}
                                    style={{
                                        position: "absolute",
                                        top: 8,
                                        zIndex: 2,
                                        background: "rgba(255,255,255,.92)",
                                        boxShadow: "0 2px 8px rgba(0,0,0,.12)",
                                        ...deleteButtonPosition,
                                    }}
                                />
                            )}
                            <Image
                                src={currentImage.url}
                                preview={true}
                                style={{ width: "100%", maxHeight: 300, objectFit: "contain" }}
                                alt="Old"
                            />
                        </div>
                    </div>
                )}

                {newImageUrl && (
                    <div style={{ textAlign: "center", flex: "1 1 300px" }}>
                        <h4>{t("new_image")}</h4>
                        <div style={{ position: "relative" }}>
                            {onRemoveNewImage && (
                                <Button
                                    type="text"
                                    danger
                                    shape="circle"
                                    icon={<DeleteOutlined />}
                                    aria-label={t("delete")}
                                    title={t("delete")}
                                    onClick={async () => {
                                        const shouldRestoreCurrentImage = !isCurrentImageHidden;
                                        await onRemoveNewImage(shouldRestoreCurrentImage);
                                        setIsCurrentImageHidden(!shouldRestoreCurrentImage);
                                    }}
                                    style={{
                                        position: "absolute",
                                        top: 8,
                                        zIndex: 2,
                                        background: "rgba(255,255,255,.92)",
                                        boxShadow: "0 2px 8px rgba(0,0,0,.12)",
                                        ...deleteButtonPosition,
                                    }}
                                />
                            )}
                            <Image
                                src={newImageUrl}
                                preview={true}
                                style={{ width: "100%", maxHeight: 300, objectFit: "contain" }}
                                alt="New"
                            />
                        </div>
                    </div>
                )}
            </div>

            {isChanged && newImageUrl && (
                <Col span={24} >
                    <Button
                        type="primary"
                        onClick={onUpload}
                        style={{ marginTop: 16, marginBottom: 8, display: "block" }}
                        loading={isImageLoading}
                    >
                        {t("upload_new_image")}
                    </Button>
                </Col>
            )}

            {showProcessingMode && <Col span={24}>
                <FormItem label={t("image_processing_type")}>
                    <Radio.Group
                        value={processingMode}
                        optionType="button"
                        buttonStyle="solid"
                        onChange={(e) => {
                            const mode = e.target.value;
                            setWithoutBackgrounds?.(mode === ImageProcessingMode.WithoutBackground);
                            setPreserveOriginal?.(mode === ImageProcessingMode.Original);
                        }}
                    >
                        <Radio.Button value={ImageProcessingMode.Avif}>
                            {t("image_processing_avif")}
                        </Radio.Button>
                        <Radio.Button value={ImageProcessingMode.Original}>
                            {t("preserve_original")}
                        </Radio.Button>
                        <Radio.Button value={ImageProcessingMode.WithoutBackground}>
                            {t("without_background")}
                        </Radio.Button>
                    </Radio.Group>
                </FormItem>
            </Col>}
        </React.Fragment>
    );
};

export default ImageFieldBlock;
